import { useState, useCallback, useRef, useEffect } from "react";
import classNames from "classNames";
import * as React from "react";

export interface IAriaTooltip {
    id?: string;
    cssWrapper?: string;
    buttonText?: string;
    showCloseButton?: boolean;
    /**
     * How the tooltip popover is triggered to show and hide:
     * - "hover": show/hide when user move the mouse in/out the tooltip label
     * - "click": show when user click the tooltip label; hide when click outside the label
     * Default: "click"
     */
    trigger?: "hover" | "click";
    /**
     * Specified the title to display when mouse hover over the tooltip.
     * When its type is string, set it as the HTML title attribute; 
     * When its type is boolean, if it is not false then set the default title.
     */
    title?: string | boolean;
    onClick?: () => void;
    onClose?: () => void;
    isLoading?: boolean;
}

export const AriaTooltip: React.FunctionComponent<IAriaTooltip> = (props) => {
    const [visible, setVisibility] = useState(false);
    const [orientation, setOrientation] = useState("to-the-right");
    const [componentMounted, setComponentMounted] = useState(false);

    const wrapperClasses = classNames(
        "aria-tooltip-wrapper",
        { "is-expanded": visible },
        { "is-dismissable": props.showCloseButton },
        props.cssWrapper,
        orientation
    );

    const wrapperRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleClickOutside = (event: any) => {
        if (!wrapperRef.current) { return; }
        if (wrapperRef && !wrapperRef.current.contains(event.target)) { closeTooltip(); }
    };
    const keyStrokesWhileOpen = (event: any) => {
        if (event.keyCode === 27) { closeTooltip(); }
    };
    const toggleVisibility = () => {
        if (visible) {
            document.removeEventListener("touchstart", handleClickOutside);
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", keyStrokesWhileOpen);
            closeTooltip();
        } else {
            if (buttonRef.current) {
                const btnRect = buttonRef.current.getBoundingClientRect();
                setOrientation(btnRect.left + 270 > window.innerWidth ? "to-the-left" : "to-the-right");
            }
            document.addEventListener("keydown", keyStrokesWhileOpen, false);
            document.addEventListener("touchstart", handleClickOutside);
            document.addEventListener("mousedown", handleClickOutside);
            setVisibility(true);
        }
    };

    const closeTooltip = () => {
        setVisibility(false);
        props?.onClose();
    };

    useEffect(() => {
        if (!componentMounted) {
            //making sure we don't mess with focus if this is just component being mounted
            setComponentMounted(true);
            return;
        }
        if (visible) {
            if (contentRef.current) { contentRef.current.focus(); }
        } else {
            if (buttonRef.current) { buttonRef.current.focus(); }
        }
    }, [visible]);

    const onButtonClick = () => {
        if (props.trigger !== 'hover') {
            toggleVisibility();
        }
        props.onClick();
    }

    const onButtonMouseEnter = useCallback(() => {
        if (props.trigger === 'hover') {
            setVisibility(true);
        }
    }, [props.trigger, setVisibility]);

    const onButtonMouseLeave = useCallback(() => {
        if (props.trigger === 'hover') {
            setVisibility(false);
            props.onClose();
        }
    }, [props.trigger, setVisibility, props.onClose]);

    const title: string | undefined =
        typeof props.title === 'string' ? props.title : props.title === false ? undefined : 'Tooltip';

    return (
        <div className={wrapperClasses} ref={wrapperRef}>
            <button
                ref={buttonRef}
                className="tooltip-button"
                aria-label={title}
                onClick={onButtonClick}
                onMouseEnter={onButtonMouseEnter}
                onMouseLeave={onButtonMouseLeave}
            >
                <span className="icon" aria-hidden={true} />
                <span className="text">{props.buttonText}</span>
            </button>
            <div className="tooltip-content" role="tooltip" ref={contentRef} tabIndex={-1}>
                <div className="tooltip-content-ar1" aria-hidden={true} />
                <div className="tooltip-content-panel">{props.isLoading ? <><div className="skeleton h20 mt-10" /><div className="skeleton h20" /><div className="skeleton h20" /><div className="skeleton h20" /></> : props.children}</div>
                <div className="tooltip-content-ar2" aria-hidden={true} />
                {props.showCloseButton && (
                    <button className="tooltip-close-button" onClick={() => closeTooltip()}>
                        <span className="icon" aria-hidden={true}></span>
                        <span className="text">Dismiss this tooltip</span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default AriaTooltip;
